import { polygonRingFromImgPosCoords } from '@/services/defect/defectMapIrAlignment';
import imageCoordinateCalculator from '@/utils/imageCoordinateCalculator';
import { projectModuleRingToIrQuad } from '@/features/defect-drawer/utils/projectModuleRingToIrQuad';

export const IR_FRAME_ZONES = ['zone1', 'zone2', 'zone3'];
const IR_FRAME_ZONE_RANK = { zone1: 0, zone2: 1, zone3: 2 };

/**
 * 사진 프레임 정규화 좌표 → 존1(안쪽) / 존2 / 존3(가장자리).
 * 기준은 모듈 중심점 1개. t = max(|nx-0.5|, |ny-0.5|) × 2
 * @param {number} normalizedX
 * @param {number} normalizedY
 * @returns {'zone1'|'zone2'|'zone3'}
 */
export function classifyIrFrameZone(normalizedX, normalizedY) {
  const nx = Number(normalizedX);
  const ny = Number(normalizedY);
  if (!Number.isFinite(nx) || !Number.isFinite(ny)) {
    return 'zone3';
  }
  const t = Math.max(Math.abs(nx - 0.5), Math.abs(ny - 0.5)) * 2;
  if (t < 1 / 3) {
    return 'zone1';
  }
  if (t < 2 / 3) {
    return 'zone2';
  }
  return 'zone3';
}

function irFrameZoneT(normalizedX, normalizedY) {
  const nx = Number(normalizedX);
  const ny = Number(normalizedY);
  if (!Number.isFinite(nx) || !Number.isFinite(ny)) {
    return 1;
  }
  return Math.max(Math.abs(nx - 0.5), Math.abs(ny - 0.5)) * 2;
}

/** 위도 보정한 평면 거리 제곱. 같은 현장 안에서 IR 중심 비교용. */
function planDistanceSq(a, b) {
  if (!a || !b) {
    return Number.POSITIVE_INFINITY;
  }
  const midLat = ((Number(a[1]) + Number(b[1])) / 2) * (Math.PI / 180);
  const dx = (Number(a[0]) - Number(b[0])) * Math.cos(midLat);
  const dy = Number(a[1]) - Number(b[1]);
  if (![dx, dy].every(Number.isFinite)) {
    return Number.POSITIVE_INFINITY;
  }
  return dx * dx + dy * dy;
}

function compareZoneThenT(a, b) {
  const zoneDiff = IR_FRAME_ZONE_RANK[a.zone] - IR_FRAME_ZONE_RANK[b.zone];
  if (zoneDiff !== 0) {
    return zoneDiff;
  }
  return a.t - b.t;
}

/**
 * @param {object[]} items
 * @param {string} [zoneFilter]
 * @returns {{ zone: 'zone1'|'zone2'|'zone3', items: { image: object, index: number }[] }[]}
 */
export function groupCoveringImagesByFrameZone(items, zoneFilter = 'all') {
  const buckets = { zone1: [], zone2: [], zone3: [] };
  (items || []).forEach((image, index) => {
    const zone = IR_FRAME_ZONES.includes(image?.frameZone) ? image.frameZone : 'zone3';
    buckets[zone].push({ image, index });
  });
  return IR_FRAME_ZONES.filter(zone => buckets[zone].length).map(zone => ({
    zone,
    items: buckets[zone]
  })).filter(group => !zoneFilter || zoneFilter === 'all' || group.zone === zoneFilter);
}

/**
 * imgPosCoords 링 → 사진 모서리.
 * [0]=왼쪽 위, [1]=오른쪽 위, [2]=오른쪽 아래, [3]=왼쪽 아래.
 * 북쪽/서쪽 정렬이 아니라 JPEG 픽셀 축과 같다.
 * @param {number[][]} ring
 * @returns {{ topLeft: number[], topRight: number[], bottomRight: number[], bottomLeft: number[] } | null}
 */
export function imageCornersFromRingOrder(ring) {
  if (!Array.isArray(ring) || ring.length < 4) {
    return null;
  }
  const first = ring[0];
  const last = ring[ring.length - 1];
  const open =
    first &&
    last &&
    Number(first[0]) === Number(last[0]) &&
    Number(first[1]) === Number(last[1])
      ? ring.slice(0, -1)
      : ring;
  if (open.length < 4) {
    return null;
  }
  const pts = open.slice(0, 4).map(pt => [Number(pt[0]), Number(pt[1])]);
  if (pts.some(pt => !Number.isFinite(pt[0]) || !Number.isFinite(pt[1]))) {
    return null;
  }
  return {
    topLeft: pts[0],
    topRight: pts[1],
    bottomRight: pts[2],
    bottomLeft: pts[3]
  };
}

function projectModuleToIrNormalized(image, lon, lat, irRing) {
  const width = Math.max(1, Number(image?.imgWidth) || 640);
  const height = Math.max(1, Number(image?.imgHeight) || 512);
  const corners = imageCornersFromRingOrder(irRing);
  if (!corners) {
    return null;
  }
  const px = imageCoordinateCalculator.geographicToPixel(
    { width, height },
    lon,
    lat,
    irRing,
    corners
  );
  if (!px) {
    return null;
  }
  return { nx: px.normalizedX, ny: px.normalizedY };
}

/**
 * @param {number[][]} ring
 * @returns {[number, number] | null}
 */
export function ringCentroid(ring) {
  if (!Array.isArray(ring) || ring.length < 3) {
    return null;
  }
  const first = ring[0];
  const last = ring[ring.length - 1];
  const pts =
    first &&
    last &&
    Number(first[0]) === Number(last[0]) &&
    Number(first[1]) === Number(last[1])
      ? ring.slice(0, -1)
      : ring;
  if (pts.length === 0) {
    return null;
  }
  let sx = 0;
  let sy = 0;
  let count = 0;
  for (const pt of pts) {
    const x = Number(pt?.[0]);
    const y = Number(pt?.[1]);
    if (!Number.isFinite(x) || !Number.isFinite(y)) {
      continue;
    }
    sx += x;
    sy += y;
    count += 1;
  }
  if (count === 0) {
    return null;
  }
  return [sx / count, sy / count];
}

/**
 * Even-odd ray casting. ring is [[lon, lat], ...].
 * @param {number} lon
 * @param {number} lat
 * @param {number[][]} ring
 */
function ringBbox(ring) {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const pt of ring) {
    const x = Number(pt?.[0]);
    const y = Number(pt?.[1]);
    if (!Number.isFinite(x) || !Number.isFinite(y)) {
      continue;
    }
    if (x < minX) minX = x;
    if (y < minY) minY = y;
    if (x > maxX) maxX = x;
    if (y > maxY) maxY = y;
  }
  return [minX, minY, maxX, maxY];
}

function bboxesOverlap(a, b) {
  return a[0] <= b[2] && a[2] >= b[0] && a[1] <= b[3] && a[3] >= b[1];
}

function direction(p, q, r) {
  return (q[0] - p[0]) * (r[1] - p[1]) - (q[1] - p[1]) * (r[0] - p[0]);
}

function segmentsIntersect(a1, a2, b1, b2) {
  const d1 = direction(b1, b2, a1);
  const d2 = direction(b1, b2, a2);
  const d3 = direction(a1, a2, b1);
  const d4 = direction(a1, a2, b2);
  return (
    ((d1 > 0 && d2 < 0) || (d1 < 0 && d2 > 0)) &&
    ((d3 > 0 && d4 < 0) || (d3 < 0 && d4 > 0))
  );
}

function ringSegments(ring) {
  const segs = [];
  if (!Array.isArray(ring) || ring.length < 2) {
    return segs;
  }
  for (let i = 0; i < ring.length - 1; i += 1) {
    const a = ring[i];
    const b = ring[i + 1];
    if (
      Number.isFinite(Number(a?.[0])) &&
      Number.isFinite(Number(a?.[1])) &&
      Number.isFinite(Number(b?.[0])) &&
      Number.isFinite(Number(b?.[1]))
    ) {
      segs.push([a, b]);
    }
  }
  return segs;
}

/**
 * 두 링이 조금이라도 겹치면 true (포함·부분 교차).
 * @param {number[][]} ringA
 * @param {number[][]} ringB
 */
export function ringsOverlap(ringA, ringB) {
  if (!Array.isArray(ringA) || !Array.isArray(ringB) || ringA.length < 3 || ringB.length < 3) {
    return false;
  }
  const boxA = ringBbox(ringA);
  const boxB = ringBbox(ringB);
  if (!bboxesOverlap(boxA, boxB)) {
    return false;
  }
  for (const pt of ringA) {
    if (isPointInRing(Number(pt[0]), Number(pt[1]), ringB)) {
      return true;
    }
  }
  for (const pt of ringB) {
    if (isPointInRing(Number(pt[0]), Number(pt[1]), ringA)) {
      return true;
    }
  }
  for (const [a1, a2] of ringSegments(ringA)) {
    for (const [b1, b2] of ringSegments(ringB)) {
      if (segmentsIntersect(a1, a2, b1, b2)) {
        return true;
      }
    }
  }
  return false;
}

export function isPointInRing(lon, lat, ring) {
  if (!Number.isFinite(lon) || !Number.isFinite(lat) || !Array.isArray(ring) || ring.length < 3) {
    return false;
  }
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const xi = Number(ring[i]?.[0]);
    const yi = Number(ring[i]?.[1]);
    const xj = Number(ring[j]?.[0]);
    const yj = Number(ring[j]?.[1]);
    if (![xi, yi, xj, yj].every(Number.isFinite)) {
      continue;
    }
    const intersect = yi > lat !== yj > lat && lon < ((xj - xi) * (lat - yi)) / (yj - yi) + xi;
    if (intersect) {
      inside = !inside;
    }
  }
  return inside;
}

/**
 * 모듈 폴리곤과 조금이라도 겹치는 IR를 프레임 존(존1→존2→존3) 순으로 반환한다.
 * @param {number[][]|null} moduleRing
 * @param {object[]} thermalImages
 * @param {{ projectToNormalized?: Function }} [options]
 * @returns {{ primary: object|null, primaryIndex: number, covering: object[] }}
 */
export function resolveModuleCoveringIrImages(moduleRing, thermalImages, options = {}) {
  const center = ringCentroid(moduleRing);
  if (!center || !Array.isArray(thermalImages) || thermalImages.length === 0) {
    return { primary: null, primaryIndex: -1, covering: [] };
  }
  const projectToNormalized =
    typeof options.projectToNormalized === 'function'
      ? options.projectToNormalized
      : projectModuleToIrNormalized;
  const scored = [];
  for (const image of thermalImages) {
    if (!image) {
      continue;
    }
    const ring = polygonRingFromImgPosCoords(image.imgPosCoords);
    if (!ring || !ringsOverlap(moduleRing, ring)) {
      continue;
    }
    const projected = projectToNormalized(image, center[0], center[1], ring);
    const nx = projected?.nx;
    const ny = projected?.ny;
    const t = irFrameZoneT(nx, ny);
    const zone = classifyIrFrameZone(nx, ny);
    const overlay = projectModuleRingToIrQuad(moduleRing, image);
    scored.push({
      image,
      t,
      zone,
      nx,
      ny,
      overlay,
      irCenter: ringCentroid(ring)
    });
  }
  const nearest = [...scored].sort((a, b) => {
    const distanceDiff = planDistanceSq(center, a.irCenter) - planDistanceSq(center, b.irCenter);
    if (distanceDiff !== 0) {
      return distanceDiff;
    }
    return compareZoneThenT(a, b);
  })[0];
  scored.sort(compareZoneThenT);
  const covering = scored.map(item => ({
    ...item.image,
    frameZone: item.zone,
    moduleFramePos:
      Number.isFinite(item.nx) && Number.isFinite(item.ny)
        ? { nx: item.nx, ny: item.ny }
        : null,
    moduleOverlayQuad: item.overlay?.quad ?? null,
    moduleOverlayProjectedQuad: item.overlay?.quad
      ? item.overlay.quad.map(pt => (Array.isArray(pt) ? [...pt] : pt))
      : null,
    moduleOverlaySource: item.overlay ? 'project' : null
  }));
  const primary =
    (nearest && covering.find(img => img.id != null && img.id === nearest.image?.id)) ||
    covering[0] ||
    null;
  const primaryIndex = primary ? covering.indexOf(primary) : -1;
  return {
    primary,
    primaryIndex,
    covering
  };
}

/**
 * @param {object[]} list
 * @param {string} [zoneFilter]
 * @returns {number[]}
 */
export function coveringIndexesForZoneFilter(list, zoneFilter = 'all') {
  if (!Array.isArray(list) || list.length === 0) {
    return [];
  }
  if (!zoneFilter || zoneFilter === 'all') {
    return list.map((_, index) => index);
  }
  const indexes = [];
  list.forEach((image, index) => {
    if (image?.frameZone === zoneFilter) {
      indexes.push(index);
    }
  });
  return indexes;
}

/**
 * @param {object[]} list
 * @param {number} currentIndex
 * @param {'next'|'previous'} direction
 * @param {string} [zoneFilter]
 * @returns {{ index: number, image: object } | null}
 */
export function resolveImageNavStep(list, currentIndex, direction, zoneFilter = 'all') {
  const indexes = coveringIndexesForZoneFilter(list, zoneFilter);
  if (indexes.length === 0) {
    return null;
  }
  const index = Number(currentIndex);
  if (!Number.isFinite(index)) {
    return null;
  }
  const pos = indexes.indexOf(index);
  const fromPos = pos >= 0 ? pos : direction === 'next' ? -1 : indexes.length;
  const nextPos = direction === 'next' ? fromPos + 1 : fromPos - 1;
  if (nextPos < 0 || nextPos >= indexes.length) {
    return null;
  }
  const nextIndex = indexes[nextPos];
  return { index: nextIndex, image: list[nextIndex] };
}

/**
 * IR 프레임에 그릴 존 1·2·3 중첩 사각형 (이미지 픽셀).
 * classifyIrFrameZone 과 같은 Chebyshev 경계: 1/3, 2/3.
 * @param {number} imageWidth
 * @param {number} imageHeight
 * @returns {{ zone: 'zone1'|'zone2'|'zone3', x: number, y: number, w: number, h: number }[]}
 */
export function irFrameZoneOverlayRects(imageWidth, imageHeight) {
  const w = Number(imageWidth);
  const h = Number(imageHeight);
  if (!Number.isFinite(w) || !Number.isFinite(h) || w <= 0 || h <= 0) {
    return [];
  }
  return [
    { zone: 'zone3', x: 0, y: 0, w, h },
    { zone: 'zone2', x: w / 6, y: h / 6, w: (w * 2) / 3, h: (h * 2) / 3 },
    { zone: 'zone1', x: w / 3, y: h / 3, w: w / 3, h: h / 3 }
  ];
}

/**
 * 존 띠 사각형 + 라벨 위치(해당 띠의 가운데).
 * @param {number} imageWidth
 * @param {number} imageHeight
 * @returns {{ zone: string, x: number, y: number, w: number, h: number, labelX: number, labelY: number }[]}
 */
export function irFrameZoneOverlayLayers(imageWidth, imageHeight) {
  const rects = irFrameZoneOverlayRects(imageWidth, imageHeight);
  if (rects.length < 3) {
    return [];
  }
  const [zone3, zone2, zone1] = rects;
  return [
    {
      ...zone3,
      labelX: zone3.x + zone3.w / 2,
      labelY: zone3.y + (zone2.y - zone3.y) / 2
    },
    {
      ...zone2,
      labelX: zone2.x + zone2.w / 2,
      labelY: zone2.y + (zone1.y - zone2.y) / 2
    },
    {
      ...zone1,
      labelX: zone1.x + zone1.w / 2,
      labelY: zone1.y + zone1.h / 2
    }
  ];
}
